import React, { useEffect, useMemo, useState } from 'react';
import { Button, Input, InputNumber, Select, Popconfirm, message, Switch } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import AppShell from './components/AppShell';
import Panel from './components/Panel';
import TableSkeleton from './components/TableSkeleton';
import { Badge } from './components/StatusPill';
import axiosInstance from './utils/axiosInstance';
import { money } from './utils/format';
import { parseSetItems, ProductSetItem } from './utils/productFilter';

interface ProductSetType {
    id: number;
    name: string;
    commission: number;
    items: ProductSetItem[] | string;
}

interface ProductOption {
    id: number;
    name: string;
    price: number;
    img: string;
}

/** Blank row for the create and add-item forms. */
const emptyDraft = () => ({
    productId: undefined as number | undefined,
    price: undefined as number | undefined,
    walletAdjustment: undefined as number | undefined,
    premium: false,
    orderConfirmTimeoutSeconds: undefined as number | undefined,
});

type Draft = ReturnType<typeof emptyDraft>;

/**
 * The 选择商品 / 自定义金额 / 钱包调整 / 商品类型 / 倒计时 row, shared by the create
 * form and every set's 添加商品 form.
 *
 * Declared at module scope on purpose. Defining it inside ProductSetPage would make
 * React see a brand-new component type on every parent render, remounting these inputs
 * and dropping focus after each keystroke.
 */
const DraftFields: React.FC<{
    value: Draft;
    options: { value: number; label: string }[];
    onChange: (patch: Partial<Draft>) => void;
}> = ({ value, options, onChange }) => (
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
        <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-muted">选择商品</span>
            <Select
                showSearch
                optionFilterProp="label"
                placeholder="选择商品"
                value={value.productId}
                onChange={(v) => onChange({ productId: v })}
                options={options}
            />
        </label>
        <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-muted">自定义金额</span>
            <InputNumber
                className="w-full"
                min={0}
                placeholder="默认使用商品价钱"
                value={value.price}
                onChange={(v) => onChange({ price: v ?? undefined })}
            />
        </label>
        <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-muted">钱包调整（可选）</span>
            <InputNumber
                className="w-full"
                placeholder="-100.00"
                value={value.walletAdjustment}
                onChange={(v) => onChange({ walletAdjustment: v ?? undefined })}
            />
        </label>
        <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-muted">商品类型</span>
            <Select
                value={value.premium}
                onChange={(v) => onChange({ premium: v })}
                options={[
                    { value: false, label: '普通' },
                    { value: true, label: '高级' },
                ]}
            />
        </label>
        <label className="flex flex-col gap-1 items-start">
            <span className="text-xs font-medium text-muted">订单确认倒计时</span>
            <div className="flex items-center gap-2 w-full mt-1">
                <Switch 
                    checked={value.orderConfirmTimeoutSeconds !== 0} 
                    onChange={(checked) => onChange({ orderConfirmTimeoutSeconds: checked ? undefined : 0 })} 
                    checkedChildren="开" 
                    unCheckedChildren="关"
                />
                {value.orderConfirmTimeoutSeconds !== 0 && (
                    <InputNumber
                        className="flex-1"
                        min={1}
                        placeholder="默认 20 秒"
                        value={value.orderConfirmTimeoutSeconds}
                        onChange={(v) => onChange({ orderConfirmTimeoutSeconds: v ?? undefined })}
                    />
                )}
            </div>
        </label>
    </div>
);

const ProductSetPage: React.FC = () => {
    const [sets, setSets] = useState<ProductSetType[]>([]);
    const [products, setProducts] = useState<ProductOption[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState<number | 'new' | null>(null);
    const [selectedSetId, setSelectedSetId] = useState<number | 'all'>('all');

    // Per-set working copies so edits are local until 保存.
    const [drafts, setDrafts] = useState<Record<number, { name: string; commission: number; items: ProductSetItem[] }>>({});
    const [addDrafts, setAddDrafts] = useState<Record<number, ReturnType<typeof emptyDraft>>>({});
    const [createDraft, setCreateDraft] = useState({ name: '', commission: 30, ...emptyDraft() });

    const fetchAll = async () => {
        setLoading(true);
        try {
            const [setsRes, productsRes] = await Promise.all([
                axiosInstance.post('admin/protected/getProductSets'),
                axiosInstance.post('admin/protected/allProducts', {}),
            ]);
            const rawSets: ProductSetType[] = setsRes.data?.data || [];
            setSets(rawSets);
            setProducts(productsRes.data?.data || []);

            const nextDrafts: Record<number, { name: string; commission: number; items: ProductSetItem[] }> = {};
            const nextAdds: Record<number, ReturnType<typeof emptyDraft>> = {};
            rawSets.forEach(s => {
                nextDrafts[s.id] = { name: s.name, commission: s.commission, items: parseSetItems(s.items) };
                nextAdds[s.id] = emptyDraft();
            });
            setDrafts(nextDrafts);
            setAddDrafts(nextAdds);
        } catch (e) {
            message.error('获取配套失败');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const productById = useMemo(() => {
        const m: Record<number, ProductOption> = {};
        products.forEach(p => { m[p.id] = p; });
        return m;
    }, [products]);

    const productOptions = useMemo(
        () => products.map(p => ({ value: p.id, label: `${p.name} | RM ${money(Number(p.price))}` })),
        [products]
    );

    /** Persists a set's current draft through the existing updateProductSet endpoint. */
    const persist = async (id: number, items: ProductSetItem[], name?: string, commission?: number) => {
        const draft = drafts[id];
        setSaving(id);
        try {
            await axiosInstance.post('admin/protected/updateProductSet', {
                id,
                name: name ?? draft.name,
                commission: commission ?? draft.commission,
                items,
            });
            message.success('配套已保存');
            fetchAll();
        } catch (e) {
            message.error('保存配套失败');
        } finally {
            setSaving(null);
        }
    };

    const draftToItem = (d: ReturnType<typeof emptyDraft>): ProductSetItem | null => {
        if (!d.productId) {
            message.error('请选择商品');
            return null;
        }
        const product = productById[d.productId];
        const item: ProductSetItem = {
            productId: d.productId,
            name: product ? product.name : '',
            price: Number.isFinite(Number(d.price)) ? Number(d.price) : Number(product?.price ?? 0),
            premium: Boolean(d.premium),
        };
        if (d.walletAdjustment !== undefined && Number.isFinite(Number(d.walletAdjustment))) {
            item.walletAdjustment = Number(d.walletAdjustment);
        }
        item.orderConfirmTimeoutSeconds =
            d.orderConfirmTimeoutSeconds === undefined || d.orderConfirmTimeoutSeconds === null
                ? null
                : Number(d.orderConfirmTimeoutSeconds);
        return item;
    };

    const handleCreate = async () => {
        if (!createDraft.name.trim()) {
            message.error('请填写套餐名称');
            return;
        }
        const item = draftToItem(createDraft);
        if (!item) return;
        setSaving('new');
        try {
            await axiosInstance.post('admin/protected/createProductSet', {
                name: createDraft.name.trim(),
                commission: Number(createDraft.commission) || 0,
                items: [item],
            });
            message.success('套餐已创建');
            setCreateDraft({ name: '', commission: 30, ...emptyDraft() });
            fetchAll();
        } catch (e) {
            message.error('创建套餐失败');
        } finally {
            setSaving(null);
        }
    };

    const handleDeleteSet = async (id: number) => {
        try {
            await axiosInstance.post('admin/protected/deleteProductSet', { id });
            message.success('套餐已删除');
            fetchAll();
        } catch (e) {
            message.error('删除套餐失败');
        }
    };

    const handleAddItem = (setId: number) => {
        const item = draftToItem(addDrafts[setId]);
        if (!item) return;
        persist(setId, [...drafts[setId].items, item]);
    };

    const handleRemoveItem = (setId: number, index: number) => {
        persist(setId, drafts[setId].items.filter((_, i) => i !== index));
    };

    const patchItem = (setId: number, index: number, patch: Partial<ProductSetItem>) => {
        setDrafts(prev => ({
            ...prev,
            [setId]: {
                ...prev[setId],
                items: prev[setId].items.map((it, i) => (i === index ? { ...it, ...patch } : it)),
            },
        }));
    };

    const patchDraft = (setId: number, patch: Partial<{ name: string; commission: number }>) => {
        setDrafts(prev => ({ ...prev, [setId]: { ...prev[setId], ...patch } }));
    };

    const patchAdd = (setId: number, patch: Partial<ReturnType<typeof emptyDraft>>) => {
        setAddDrafts(prev => ({ ...prev, [setId]: { ...prev[setId], ...patch } }));
    };

    return (
        <AppShell title="套餐配置" subtitle={`${sets.length} 个套餐`}>
            <Panel className="mb-4">
                <div className="mb-3 text-[13px] font-semibold text-ink">创建套餐</div>
                <div className="mb-3 grid gap-3 sm:grid-cols-2">
                    <label className="flex flex-col gap-1">
                        <span className="text-xs font-medium text-muted">套餐名称</span>
                        <Input
                            placeholder="例如：Set C"
                            value={createDraft.name}
                            onChange={(e) => setCreateDraft({ ...createDraft, name: e.target.value })}
                        />
                    </label>
                    <label className="flex flex-col gap-1">
                        <span className="text-xs font-medium text-muted">佣金比例 (%)</span>
                        <InputNumber
                            className="w-full"
                            min={0}
                            value={createDraft.commission}
                            onChange={(v) => setCreateDraft({ ...createDraft, commission: Number(v ?? 0) })}
                        />
                    </label>
                </div>
                <DraftFields
                    value={createDraft}
                    options={productOptions}
                    onChange={(patch) => setCreateDraft({ ...createDraft, ...patch })}
                />
                <div className="mt-4">
                    <Button type="primary" icon={<PlusOutlined />} loading={saving === 'new'} onClick={handleCreate}>
                        创建套餐
                    </Button>
                </div>
            </Panel>

            {loading ? (
                <Panel><TableSkeleton rows={4} columns={4} /></Panel>
            ) : sets.length === 0 ? (
                <Panel><div className="py-8 text-center text-sm text-muted">暂无套餐</div></Panel>
            ) : (
                <>
                    <div className="mb-4 flex flex-wrap gap-2">
                        <Button 
                            type={selectedSetId === 'all' ? 'primary' : 'default'} 
                            onClick={() => setSelectedSetId('all')}
                        >
                            全部 (All)
                        </Button>
                        {sets.map(s => (
                            <Button 
                                key={s.id} 
                                type={selectedSetId === s.id ? 'primary' : 'default'}
                                onClick={() => setSelectedSetId(s.id)}
                            >
                                {s.name}
                            </Button>
                        ))}
                    </div>
                    {sets.filter(s => selectedSetId === 'all' || s.id === selectedSetId).map((set) => {
                        const draft = drafts[set.id];
                    if (!draft) return null;
                    return (
                        <Panel key={set.id} className="mb-4">
                            <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
                                <div>
                                    <div className="text-[15px] font-semibold text-ink">
                                        {set.name} <span className="text-muted">(编号{set.id})</span>
                                    </div>
                                    <div className="mt-0.5 text-xs text-muted">
                                        佣金 {set.commission}% · {draft.items.length} 个商品
                                    </div>
                                </div>
                                <Popconfirm
                                    title="确定删除此套餐？"
                                    okText="确定"
                                    cancelText="取消"
                                    onConfirm={() => handleDeleteSet(set.id)}
                                >
                                    <Button size="small" danger icon={<DeleteOutlined />}>删除套餐</Button>
                                </Popconfirm>
                            </div>

                            <div className="mb-4 grid items-end gap-3 sm:grid-cols-3">
                                <label className="flex flex-col gap-1">
                                    <span className="text-xs font-medium text-muted">套餐名称</span>
                                    <Input
                                        value={draft.name}
                                        onChange={(e) => patchDraft(set.id, { name: e.target.value })}
                                    />
                                </label>
                                <label className="flex flex-col gap-1">
                                    <span className="text-xs font-medium text-muted">佣金比例 (%)</span>
                                    <InputNumber
                                        className="w-full"
                                        min={0}
                                        value={draft.commission}
                                        onChange={(v) => patchDraft(set.id, { commission: Number(v ?? 0) })}
                                    />
                                </label>
                                <div>
                                    <Button
                                        loading={saving === set.id}
                                        onClick={() => persist(set.id, draft.items)}
                                    >
                                        保存
                                    </Button>
                                </div>
                            </div>

                            <div className="overflow-x-auto rounded-lg border border-line">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="border-b border-line bg-slate-50/60 text-left text-xs text-muted">
                                            <th className="px-3 py-2 font-medium">图片</th>
                                            <th className="px-3 py-2 font-medium">商品</th>
                                            <th className="px-3 py-2 font-medium">金额 / 钱包 / 类型 / 倒计时</th>
                                            <th className="px-3 py-2 font-medium">操作</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {draft.items.length === 0 && (
                                            <tr>
                                                <td colSpan={4} className="px-3 py-6 text-center text-xs text-muted">
                                                    暂无商品
                                                </td>
                                            </tr>
                                        )}
                                        {draft.items.map((item, index) => {
                                            const product = item.productId ? productById[item.productId] : undefined;
                                            return (
                                                <tr key={`${item.name}-${index}`} className="border-b border-line last:border-0 align-top">
                                                    <td className="px-3 py-3">
                                                        {product ? (
                                                            <div className="h-14 w-14 overflow-hidden rounded-md border border-line">
                                                                <img
                                                                    alt={product.name}
                                                                    src={`${process.env.REACT_APP_FOLDER_URL}/${product.img}`}
                                                                    className="h-full w-full object-cover"
                                                                />
                                                            </div>
                                                        ) : (
                                                            <div className="flex h-14 w-14 items-center justify-center rounded-md border border-line bg-slate-50 text-[10px] text-faint">
                                                                无图片
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="px-3 py-3">
                                                        <div className="font-semibold text-ink">{item.name}</div>
                                                        <div className="mt-0.5 text-xs text-muted">
                                                            {item.productId ? `商品 ID #${item.productId}` : '按名称匹配'}
                                                        </div>
                                                        {item.productId && !product && (
                                                            <div className="mt-1 text-xs text-caution">商品已删除</div>
                                                        )}
                                                        {product && product.name !== item.name && (
                                                            <div className="mt-1 text-xs text-caution">
                                                                名称已变更，分配时将匹配不到
                                                            </div>
                                                        )}
                                                        <div className="mt-1">
                                                            <Badge tone={item.premium ? 'caution' : 'neutral'}>
                                                                {item.premium ? '高级' : '普通'}
                                                            </Badge>
                                                        </div>
                                                    </td>
                                                    <td className="px-3 py-3">
                                                        <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
                                                            <InputNumber
                                                                className="w-full"
                                                                min={0}
                                                                value={item.price}
                                                                onChange={(v) => patchItem(set.id, index, { price: Number(v ?? 0) })}
                                                            />
                                                            <InputNumber
                                                                className="w-full"
                                                                placeholder="钱包调整"
                                                                value={item.walletAdjustment}
                                                                onChange={(v) => patchItem(set.id, index, { walletAdjustment: v ?? undefined })}
                                                            />
                                                            <Select
                                                                value={Boolean(item.premium)}
                                                                onChange={(v) => patchItem(set.id, index, { premium: v })}
                                                                options={[
                                                                    { value: false, label: '普通' },
                                                                    { value: true, label: '高级' },
                                                                ]}
                                                            />
                                                            <InputNumber
                                                                className="w-full"
                                                                min={0}
                                                                placeholder="默认 20 秒"
                                                                value={item.orderConfirmTimeoutSeconds ?? undefined}
                                                                onChange={(v) => patchItem(set.id, index, {
                                                                    orderConfirmTimeoutSeconds: v === null || v === undefined ? null : Number(v),
                                                                })}
                                                            />
                                                        </div>
                                                    </td>
                                                    <td className="px-3 py-3">
                                                        <div className="flex flex-wrap gap-2">
                                                            <Button
                                                                size="small"
                                                                loading={saving === set.id}
                                                                onClick={() => persist(set.id, draft.items)}
                                                            >
                                                                保存
                                                            </Button>
                                                            <Popconfirm
                                                                title="确定移除此商品？"
                                                                okText="确定"
                                                                cancelText="取消"
                                                                onConfirm={() => handleRemoveItem(set.id, index)}
                                                            >
                                                                <Button size="small" danger>移除</Button>
                                                            </Popconfirm>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            <div className="mt-4 rounded-lg border border-dashed border-line p-3">
                                <div className="mb-3 text-[13px] font-semibold text-ink">添加商品</div>
                                <DraftFields
                                    value={addDrafts[set.id] || emptyDraft()}
                                    options={productOptions}
                                    onChange={(patch) => patchAdd(set.id, patch)}
                                />
                                <div className="mt-3">
                                    <Button
                                        type="primary"
                                        icon={<PlusOutlined />}
                                        loading={saving === set.id}
                                        onClick={() => handleAddItem(set.id)}
                                    >
                                        添加到套餐
                                    </Button>
                                </div>
                            </div>
                        </Panel>
                    );
                })
                }
                </>
            )}
        </AppShell>
    );
};

export default ProductSetPage;
